/* AZCP vehicle filter — WEBHOOK-DRIVEN cascade (v4).

   Make→Model→Engine→Year options come on-demand from the n8n cascade API
   (relationship-correct, sourced from the KType master), so there is no giant
   client-side index and no cross-make contamination. On submit the selection is
   resolved to KType(s) and results are filtered by the hidden
   `filter.p.m.custom.ktypes` facet. Category (product_type) stays an independent
   native filter, sourced from the collection facets.

   API (GET, returns JSON):
     ?field=make                                  -> { options:[...] }
     ?field=model&make=X                          -> { options:[...] }
     ?field=engine&make=X&model=Y                 -> { options:[...] }
     ?field=year&make=X&model=Y[&engine=Z]        -> { options:[...] }
     ?field=resolve&make=X&model=Y[&engine][&year]-> { ktypes:[...] }

   Reads window.AZCP.pure (sanitizeEngineLabel, parseFacets) + window.AZCP.savedVehicle. */
(function () {
  'use strict';
  var pure = window.AZCP && window.AZCP.pure;
  var savedVehicleApi = window.AZCP && window.AZCP.savedVehicle;

  var FIELDS = ['make', 'model', 'engine', 'year', 'category'];
  var PARAM = {
    category: 'filter.p.product_type',
    make: 'filter.p.m.custom.make',
    model: 'filter.p.m.custom.model',
    engine: 'filter.p.m.custom.engine',
    year: 'filter.p.m.custom.year'
  };
  var KTYPE_PARAM = 'filter.p.m.custom.ktypes';
  // Vehicle-match mode. false = legacy ktype filter (capped at 128/product).
  // true = cap-proof "Make|Model" key in custom.models — flip to true ONLY after
  // custom.models is enabled as a Search & Discovery filter AND reindexed. This
  // flag flips BOTH the category source and the search filter together so they
  // stay consistent.
  var USE_MODELS = true;
  var DRAFT_KEY = 'azcp.cascadeDraft.v1';
  // Product types that are supplier/brand names rather than real part categories.
  // These are hidden from the vehicle's category dropdown (the underlying products
  // keep their type and still appear in an unfiltered vehicle search). Add new
  // brand/supplier names here as they appear in the catalogue.
  var CATEGORY_BLOCKLIST = {};
  ['A-Z Assembled', 'A-Z Assembled / Transtech', 'BILSTEIN', 'Brembo',
   'Crank Technincs', 'Cranktechnics', 'Delphi', 'Dunlop', 'Febi Bilstein',
   'Febi | Blue Print | Millers Oil', 'Gates', 'Gates / Ina', 'Hi-Torq',
   'Hi-Torque', 'Hi-Torque Stage 1', 'Kinetix', 'Kinetix GT Performance',
   'Kinetix GT Performance | Mintex', 'Kinetix GT Performance | Mintex Racing',
   'Kinetix OEQ+', 'Kinetix Performance', 'Millers Oil', 'OEQ+', 'PCS', 'Pcs',
   'PCS Europe', 'PCS OEQ+', 'Textar', 'Transtech', 'Transtech Germany',
   'Transtech | Millers Premium Oil'].forEach(function (s) { CATEGORY_BLOCKLIST[s.toLowerCase()] = true; });
  // Shared option cache (in-memory + sessionStorage) so dropdowns load instantly
  // on repeat selections and across page navigations. Only option lookups are
  // cached (not 'resolve', which is a one-shot submit action).
  var _CACHE = {};
  var CACHE_PREFIX = 'azcp.casc.';

  document.querySelectorAll('[data-azcp-filter]').forEach(initFilter);

  function initFilter(root) {
    var API = root.dataset.cascadeApi;
    var collectionUrl = root.dataset.collectionUrl;
    var sectionId = root.dataset.sectionId;
    var noAutoFill = root.dataset.noAutoFill === 'true';
    // Server-rendered product_type fallback (used when the collection section
    // renders no facets, e.g. large collections like with-filters).
    var categoryFallback = (function () {
      try {
        var parsed = JSON.parse(root.dataset.categoryOptions || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) { return []; }
    })();
    var form = root.querySelector('[data-azcp-filter-form]');
    var submitBtn = root.querySelector('.azcp-filter__submit');
    var live = root.querySelector('[data-live]');
    var selects = {};
    FIELDS.forEach(function (f) { selects[f] = root.querySelector('[data-select="' + f + '"]'); });
    var pendingDraft = null;
    var seq = 0; // guards against out-of-order async responses
    var catSeq = 0; // separate guard for the (independent) category fetch

    // Restore the customer's selection after navigation (e.g. after "Find parts"
    // reloads the page): prefer the saved vehicle (persisted to localStorage on
    // submit), else the in-session draft. This keeps the dropdowns populated with
    // the chosen vehicle instead of resetting on every collection page.
    var _sv = (savedVehicleApi && savedVehicleApi.get) ? savedVehicleApi.get() : null;
    if (_sv && _sv.make) {
      pendingDraft = { make: _sv.make, model: _sv.model, engine: _sv.engine, year: _sv.year };
    } else if (!noAutoFill) {
      restoreDraft();
    }

    selects.make.addEventListener('change', onMake);
    selects.model.addEventListener('change', onModel);
    selects.engine.addEventListener('change', onEngine);
    selects.year.addEventListener('change', function () { updateProgress(); updateSubmit(); saveDraft(); refreshCategories(); });
    selects.category.addEventListener('change', onCategory);
    form.addEventListener('submit', onSubmit);

    // Boot: load makes + category options in parallel. Category is an optional
    // FINAL filter (after the vehicle), so makes are never category-scoped.
    setOptions(selects.make, [], 'Loading…');
    setLoading('make', true);
    // Category is vehicle-scoped: locked until a vehicle is chosen, then populated
    // (by refreshCategories) with ONLY the product types that have parts for that
    // vehicle. It is never restored from the URL on the results page, so the
    // shopper can start a fresh search.
    clearField('category', 'Pick a vehicle first');
    apiCall({ field: 'make' })
      .then(function (d) {
        setFieldOptions('make', toOpts((d || {}).options));
        setLocked('make', false);
        updateProgress();
        updateSubmit();
        if (pendingDraft) applyDraft();
        announce('Choose your vehicle');
      })
      .catch(function () { setLoading('make', false); announce("Couldn't load vehicle data."); });

    // ---------- API ----------
    function apiCall(params) {
      var u = new URL(API);
      Object.keys(params).forEach(function (k) { if (params[k]) u.searchParams.set(k, params[k]); });
      var key = u.toString();
      // resolve is cacheable too now: it's reused to populate the vehicle-scoped
      // category list (refreshCategories) and again on submit for the same vehicle.
      var cacheable = true;
      if (cacheable) {
        if (_CACHE[key]) return Promise.resolve(_CACHE[key]);
        try { var raw = sessionStorage.getItem(CACHE_PREFIX + key); if (raw) { var v = JSON.parse(raw); _CACHE[key] = v; return Promise.resolve(v); } } catch (e) {}
      }
      return fetch(key, { credentials: 'omit' }).then(function (r) { return r.json(); }).then(function (j) {
        if (cacheable) { _CACHE[key] = j; try { sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify(j)); } catch (e) {} }
        return j;
      });
    }
    function toOpts(arr, isEngine) {
      return (arr || []).map(function (x) {
        return { value: x, label: (isEngine && pure) ? (pure.sanitizeEngineLabel(x) || x) : x };
      });
    }
    function vals() {
      return FIELDS.reduce(function (a, f) { if (selects[f].value) a[f] = selects[f].value; return a; }, {});
    }

    // ---------- cascade steps ----------
    function onMake() {
      var mk = selects.make.value;
      clearField('model', 'Pick make first'); clearField('engine', '—'); clearField('year', '—');
      updateProgress(); updateSubmit(); saveDraft();
      refreshCategories(); // model now empty → re-locks category
      if (!mk) return;
      var s = ++seq; setLoading('model', true);
      apiCall({ field: 'model', make: mk }).then(function (d) {
        if (s !== seq) return;
        setFieldOptions('model', toOpts(d.options)); setLocked('model', (d.options || []).length === 0);
      }).catch(function () { if (s === seq) setLoading('model', false); });
    }
    function onModel() {
      var mk = selects.make.value, md = selects.model.value;
      clearField('engine', '—'); clearField('year', '—');
      updateProgress(); updateSubmit(); saveDraft();
      refreshCategories(); // make+model now set → fetch the vehicle's categories
      if (!mk || !md) return;
      var s = ++seq; setLoading('engine', true); setLoading('year', true);
      apiCall({ field: 'engine', make: mk, model: md }).then(function (d) {
        if (s !== seq) return; var o = d.options || [];
        setFieldOptions('engine', toOpts(o, true)); setLocked('engine', o.length === 0);
      }).catch(function () { if (s === seq) setLoading('engine', false); });
      apiCall({ field: 'year', make: mk, model: md }).then(function (d) {
        if (s !== seq) return; var o = d.options || [];
        setFieldOptions('year', toOpts(o)); setLocked('year', o.length === 0);
      }).catch(function () { if (s === seq) setLoading('year', false); });
    }
    function onEngine() {
      // years can narrow by engine
      var mk = selects.make.value, md = selects.model.value, en = selects.engine.value;
      updateProgress(); updateSubmit(); saveDraft();
      refreshCategories(); // engine refines the vehicle → narrow its categories
      if (!mk || !md) return;
      var s = ++seq; setLoading('year', true);
      var prevYear = selects.year.value;
      apiCall({ field: 'year', make: mk, model: md, engine: en }).then(function (d) {
        if (s !== seq) return; var o = d.options || [];
        setFieldOptions('year', toOpts(o)); setLocked('year', o.length === 0);
        if (prevYear && o.indexOf(prevYear) !== -1) selects.year.value = prevYear;
      }).catch(function () { if (s === seq) setLoading('year', false); });
    }
    function onCategory() {
      // Category is an optional FINAL filter (chosen after the vehicle). It only
      // narrows results by product_type on submit — it does NOT scope the vehicle
      // cascade, so changing it never re-fetches make/model/engine/year.
      updateProgress(); updateSubmit(); saveDraft();
    }
    // Populate the category dropdown with ONLY the product types that have parts
    // for the currently selected vehicle. Locked until make+model are chosen.
    function refreshCategories() {
      var mk = selects.make.value, md = selects.model.value;
      if (!mk || !md) { clearField('category', 'Pick a vehicle first'); return; }
      var prev = selects.category.value;
      var cs = ++catSeq;
      setLoading('category', true);
      // Compute available categories at the MAKE+MODEL level (not narrowed by the
      // optional engine/year). Otherwise refining a specific variant can shrink the
      // category list to zero even when the model has stocked part types, which
      // reads as "broken". Submit still uses the precise engine/year ktypes.
      apiCall({ field: 'resolve', make: mk, model: md })
        .then(function (d) {
          if (cs !== catSeq) return;
          var cats = ((USE_MODELS ? d.categoriesByModel : d.categories) || [])
            .filter(function (c) { return !CATEGORY_BLOCKLIST[String(c).toLowerCase()]; })
            .map(function (c) { return { value: c, label: c }; });
          setFieldOptions('category', cats);
          setLocked('category', cats.length === 0);
          if (prev && cats.some(function (c) { return c.value === prev; })) selects.category.value = prev;
          updateProgress(); updateSubmit();
        })
        .catch(function () { if (cs === catSeq) setLoading('category', false); });
    }

    // ---------- submit ----------
    function onSubmit(e) {
      e.preventDefault();
      submitBtn.classList.add('is-loading');
      var v = vals();
      apiCall({ field: 'resolve', make: v.make, model: v.model })
        .then(function (d) {
          var ktypes = d.ktypes || [];
          saveAsVehicle(ktypes);
          var url = new URL('/search', window.location.origin);
          url.searchParams.set('q', v.make);
          url.searchParams.set('type', 'product');
          if (v.category) url.searchParams.set(PARAM.category, v.category);
          // VEHICLE MATCH — gated by the module-level USE_MODELS flag (see top).
          // q=make matches every fitting product because each product carries its fitment
          // makes as TAGS (Shopify search indexes tags, not metafields) — this catches
          // rebadges like Acura Legend (titled "Honda Legend") that title-only search missed.
          // custom.models provides exact model precision via the filter below.
          if (USE_MODELS) {
            url.searchParams.set('filter.p.m.custom.models', v.make + '|' + v.model);
          } else {
            var MAX_URL = 7000;
            for (var i = 0; i < ktypes.length; i++) {
              url.searchParams.append(KTYPE_PARAM, ktypes[i]);
              if (url.toString().length > MAX_URL) break;
            }
          }
          window.location.href = url.toString();
        })
        .catch(function () { submitBtn.classList.remove('is-loading'); announce("Couldn't search. Try again."); });
    }

    // ---------- category (independent native facet) ----------
    function fetchCategoryOptions() {
      if (!pure || !pure.parseFacets) return Promise.resolve([]);
      // Large collections (>5,000 products, e.g. with-filters / all) render NO
      // facets at all, so this section fetch returns nothing usable — and it's an
      // expensive render of the whole catalogue that fires on EVERY page load
      // (the header card targets with-filters). That repeated heavy request is a
      // Cloudflare rate-limit magnet. Skip it; the server-rendered
      // `data-category-options` fallback already covers these pages.
      if (!collectionUrl || /\/collections\/(with-filters|all)(?:[\/?#]|$)/.test(collectionUrl)) {
        return Promise.resolve([]);
      }
      // On real (smaller) collection pages the scoped facet list IS useful — fetch
      // it once and cache so re-inits / back-navigation don't re-hit the server.
      var cacheKey = CACHE_PREFIX + 'cat.' + collectionUrl + '#' + sectionId;
      try { var cached = sessionStorage.getItem(cacheKey); if (cached) return Promise.resolve(JSON.parse(cached)); } catch (e) {}
      var url = new URL(collectionUrl, window.location.origin);
      url.searchParams.set('section_id', sectionId);
      return fetch(url.toString(), { credentials: 'same-origin' })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var cats = pure.parseFacets(html, { category: PARAM.category }).category || [];
          try { sessionStorage.setItem(cacheKey, JSON.stringify(cats)); } catch (e) {}
          return cats;
        })
        .catch(function () { return []; });
    }

    // ---------- DOM helpers ----------
    function setOptions(sel, options, placeholder) {
      if (!sel) return;
      var current = sel.value;
      sel.innerHTML = '';
      var ph = document.createElement('option'); ph.value = ''; ph.textContent = placeholder; sel.appendChild(ph);
      options.forEach(function (o) {
        var opt = document.createElement('option'); opt.value = o.value; opt.textContent = o.label; sel.appendChild(opt);
      });
      if (options.some(function (o) { return o.value === current; })) sel.value = current;
    }
    function setFieldOptions(field, options) {
      setOptions(selects[field], options, placeholderFor(field, options.length));
      setCount(field, options.length ? '(' + options.length + ')' : '');
      setLoading(field, false);
      var fieldEl = root.querySelector('[data-field="' + field + '"]');
      if (fieldEl) fieldEl.classList.toggle('azcp-filter__field--filled', !!selects[field].value);
    }
    function clearField(field, placeholder) {
      setOptions(selects[field], [], placeholder);
      setCount(field, ''); setLocked(field, true);
      var fieldEl = root.querySelector('[data-field="' + field + '"]');
      if (fieldEl) fieldEl.classList.remove('azcp-filter__field--filled');
    }
    function placeholderFor(field, count) {
      if (count === 0) {
        if (field === 'category') return 'No categories';
        if (field === 'model') return 'No models for this make';
        return 'No options';
      }
      if (field === 'category') return 'Choose category';
      if (field === 'make') return 'Choose make';
      if (field === 'model') return 'Choose model';
      if (field === 'engine') return 'All engines';
      if (field === 'year') return 'All years';
      return 'Choose…';
    }
    function setCount(field, text) { var el = root.querySelector('[data-count="' + field + '"]'); if (el) el.textContent = text; }
    function setLoading(field, loading) {
      var f = root.querySelector('[data-field="' + field + '"]'); var sel = selects[field];
      if (f) f.classList.toggle('azcp-filter__field--loading', !!loading);
      // Disable the dropdown while its options load so it can't be opened or
      // changed before they arrive (and can't show stale options). While loading,
      // show a visible "Loading…" placeholder in the select itself (every field —
      // make/model/engine/year/category). setFieldOptions replaces it with the real
      // options + setLoading(false) once the data arrives; callers that need to keep
      // a prior selection (onEngine year, refreshCategories) re-apply it explicitly.
      if (sel && loading) { setOptions(sel, [], 'Loading…'); sel.disabled = true; sel.setAttribute('aria-busy', 'true'); }
      else if (sel) { sel.removeAttribute('aria-busy'); }
    }
    function setLocked(field, locked) {
      var fieldEl = root.querySelector('[data-field="' + field + '"]'); var sel = selects[field];
      if (!fieldEl) return;
      fieldEl.classList.toggle('azcp-filter__field--locked', !!locked);
      if (locked) { fieldEl.setAttribute('data-locked', ''); sel.disabled = true; sel.setAttribute('aria-disabled', 'true'); }
      else { fieldEl.removeAttribute('data-locked'); sel.disabled = false; sel.removeAttribute('aria-disabled'); }
    }
    function updateProgress() {
      var v = vals();
      FIELDS.forEach(function (f) {
        var seg = root.querySelector('[data-segment="' + f + '"]'); if (seg) seg.classList.toggle('is-filled', !!v[f]);
        root.querySelector('[data-field="' + f + '"]').classList.remove('azcp-filter__field--active');
      });
      for (var i = 0; i < FIELDS.length; i++) {
        if (!v[FIELDS[i]]) { var fEl = root.querySelector('[data-field="' + FIELDS[i] + '"]'); if (fEl && !fEl.hasAttribute('data-locked')) fEl.classList.add('azcp-filter__field--active'); break; }
      }
    }
    function updateSubmit() { var v = vals(); submitBtn.disabled = !(v.make && v.model); }
    function announce(msg) { if (live) live.textContent = msg; }

    // ---------- draft (for "change vehicle" pre-fill) ----------
    function saveDraft() { if (noAutoFill) return; try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(vals())); } catch (e) {} }
    function restoreDraft() { try { var raw = sessionStorage.getItem(DRAFT_KEY); if (raw) pendingDraft = JSON.parse(raw); } catch (e) {} }
    function applyDraft() {
      var dr = pendingDraft; pendingDraft = null;
      if (!dr || !dr.make) return;
      if (!optionHas(selects.make, dr.make)) return;
      selects.make.value = dr.make;
      apiCall({ field: 'model', make: dr.make }).then(function (d) {
        setFieldOptions('model', toOpts(d.options)); setLocked('model', false);
        if (dr.model && optionHas(selects.model, dr.model)) {
          selects.model.value = dr.model;
          return Promise.all([apiCall({ field: 'engine', make: dr.make, model: dr.model }), apiCall({ field: 'year', make: dr.make, model: dr.model })]);
        }
      }).then(function (r) {
        if (!r) { updateProgress(); updateSubmit(); refreshCategories(); return; }
        setFieldOptions('engine', toOpts((r[0] || {}).options, true)); setLocked('engine', false);
        setFieldOptions('year', toOpts((r[1] || {}).options)); setLocked('year', false);
        if (dr.engine && optionHas(selects.engine, dr.engine)) selects.engine.value = dr.engine;
        if (dr.year && optionHas(selects.year, dr.year)) selects.year.value = dr.year;
        updateProgress(); updateSubmit();
        // Populate the category dropdown for the restored vehicle (selection stays
        // empty/reset, but the options reflect what's available for this vehicle).
        refreshCategories();
      }).catch(function () {});
    }
    function optionHas(sel, val) { return Array.prototype.some.call(sel.options, function (o) { return o.value === val; }); }

    // Pre-select the category from the current URL's product_type param so the
    // dropdown reflects the active filter after a "Find parts" navigation.
    function restoreCategoryFromUrl() {
      var sel = selects.category;
      if (!sel) return;
      var current;
      try { current = new URLSearchParams(window.location.search).get(PARAM.category); } catch (e) { current = null; }
      if (!current) return;
      // Ensure the active value is selectable even if it's absent from a
      // collection-scoped option list.
      if (!optionHas(sel, current)) {
        var opt = document.createElement('option');
        opt.value = current; opt.textContent = current; sel.appendChild(opt);
      }
      sel.value = current;
      var fieldEl = root.querySelector('[data-field="category"]');
      if (fieldEl) fieldEl.classList.toggle('azcp-filter__field--filled', !!sel.value);
    }

    function saveAsVehicle(ktypes) {
      var v = vals();
      if (!v.make || !v.model || !savedVehicleApi || !savedVehicleApi.set) return;
      savedVehicleApi.set({
        make: v.make, model: v.model, engine: v.engine,
        engineLabel: v.engine ? (selects.engine.options[selects.engine.selectedIndex] || {}).textContent : undefined,
        year: v.year, ktype: (ktypes && ktypes.length === 1) ? ktypes[0] : undefined,
        ktypes: ktypes && ktypes.length ? ktypes : undefined, savedAt: Date.now()
      });
      try { sessionStorage.removeItem(DRAFT_KEY); } catch (e) {}
    }
  }

  /* ------------------------------------------------------------
     Mobile filter popup wiring (independent of the cascade logic).
     Toggles .is-open on the modal shell; locks body scroll; manages
     focus. The filter card is already initialised above — it lives in
     the DOM from load, just visually hidden on mobile until opened.
     ------------------------------------------------------------ */
  (function () {
    var modal = document.querySelector('[data-azcp-filter-modal]');
    var trigger = document.querySelector('[data-azcp-filter-open]');
    if (!modal || !trigger) return;

    var dialog = modal.querySelector('.azcp-filter-modal__dialog');
    var closers = modal.querySelectorAll('[data-azcp-filter-close]');
    var lastFocused = null;

    function open() {
      lastFocused = document.activeElement;
      modal.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      var first = modal.querySelector('#azcp-make') ||
        modal.querySelector('a[href], button:not([disabled]), select, input, textarea, [tabindex]:not([tabindex="-1"])');
      if (first) { try { first.focus(); } catch (e) {} }
      if (typeof window.trapFocus === 'function' && dialog) {
        try { window.trapFocus(modal, first || dialog); } catch (e) {}
      }
    }
    function close() {
      modal.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      if (typeof window.removeTrapFocus === 'function') {
        try { window.removeTrapFocus(lastFocused || trigger); } catch (e) {}
      } else if (lastFocused) {
        try { lastFocused.focus(); } catch (e) {}
      }
    }

    trigger.addEventListener('click', open);
    Array.prototype.forEach.call(closers, function (el) {
      el.addEventListener('click', close);
    });
    // Click on the overlay / outside the dialog closes.
    modal.addEventListener('click', function (e) {
      if (e.target === modal || e.target.classList.contains('azcp-filter-modal__overlay')) close();
    });
    document.addEventListener('keyup', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
    });
  }());
}());
